/**
 * PLAYER module — first-person controller: pointer lock + mouse look, WASD on
 * fixed-timestep updates, capsule-vs-AABB collision against WORLD's
 * `userData.solid` meshes, center-screen interaction raycast, zone detection,
 * and the debug teleport. Owned by PLAYER; see CONTRACTS.md.
 */
import type { Box3, Object3D } from 'three'

import {
  OFFICE_X_THRESHOLD,
  PLAYER_EYE_HEIGHT,
  PLAYER_SPAWN_PITCH,
  PLAYER_SPAWN_POSITION,
  PLAYER_SPAWN_YAW
} from '../core/constants'
import type { ModuleContext, ZoneId } from '../core/types'
import { collectSolidBoxes, resolveCapsuleAgainstBoxes } from './collision'
import {
  CAPSULE_HEIGHT,
  CAPSULE_RADIUS,
  MOUSE_SENSITIVITY,
  MOVED_EMIT_INTERVAL,
  PITCH_LIMIT,
  SCENE_CACHE_REFRESH_INTERVAL,
  WALK_SPEED
} from './constants'
import { isTouchDevice } from '../core/platform'
import { createKeyboard, createPointerLock } from './input'
import { createTouchControls } from './touch'
import {
  buildInteractTarget,
  collectInteractables,
  createHighlight,
  createInteractionProbe
} from './interaction'

const zoneFor = (x: number): ZoneId => (x > OFFICE_X_THRESHOLD ? 'indoor' : 'outdoor')

const getCanvas = (): HTMLCanvasElement | null =>
  document.querySelector<HTMLCanvasElement>('#app canvas') ?? document.querySelector('canvas')

export const initPlayer = (ctx: ModuleContext): void => {
  const { scene, camera, bus, loop, debug } = ctx

  // --- Pose state -----------------------------------------------------------
  const pose = {
    x: PLAYER_SPAWN_POSITION.x,
    y: PLAYER_SPAWN_POSITION.y,
    z: PLAYER_SPAWN_POSITION.z,
    yaw: PLAYER_SPAWN_YAW,
    pitch: PLAYER_SPAWN_PITCH
  }
  let zone: ZoneId = zoneFor(pose.x)
  let controlsEnabled = false
  let journalOpen = false
  let gameOver = false
  let poseDirty = false
  let emitTimer = MOVED_EMIT_INTERVAL

  // --- Scene caches (solid AABBs + interactables, refreshed periodically) ---
  let solidBoxes: readonly Box3[] = []
  let interactables: readonly Object3D[] = []
  let cacheTimer = SCENE_CACHE_REFRESH_INTERVAL

  const refreshSceneCaches = (): void => {
    solidBoxes = collectSolidBoxes(scene)
    interactables = collectInteractables(scene)
    cacheTimer = 0
  }

  // --- Camera + events ------------------------------------------------------
  camera.rotation.order = 'YXZ'

  const applyCameraFromPose = (): void => {
    camera.position.set(pose.x, pose.y, pose.z)
    camera.rotation.set(pose.pitch, pose.yaw, 0)
  }

  const emitMoved = (): void => {
    bus.emit('player:moved', {
      position: { x: pose.x, y: pose.y, z: pose.z },
      yaw: pose.yaw,
      pitch: pose.pitch
    })
    poseDirty = false
    emitTimer = 0
  }

  const updateZone = (): void => {
    const next = zoneFor(pose.x)
    if (next === zone) return
    zone = next
    bus.emit('zone:changed', { zone: next })
  }

  // --- Interaction ----------------------------------------------------------
  const probe = createInteractionProbe()
  const highlight = createHighlight(scene)

  const movementAllowed = (): boolean => controlsEnabled && !journalOpen && !gameOver

  const tryInteract = (): void => {
    if (!movementAllowed()) return
    const hovered = highlight.current()
    if (!hovered) return
    bus.emit('player:interact', { target: buildInteractTarget(hovered) })
  }

  // --- Input ----------------------------------------------------------------
  const keyboard = createKeyboard()
  const touchScheme = isTouchDevice()

  const applyLookDelta = (dx: number, dy: number): void => {
    if (!movementAllowed()) return
    pose.yaw -= dx * MOUSE_SENSITIVITY
    pose.pitch = Math.min(PITCH_LIMIT, Math.max(-PITCH_LIMIT, pose.pitch - dy * MOUSE_SENSITIVITY))
    poseDirty = true
    applyCameraFromPose()
  }

  const pointerLock = createPointerLock(getCanvas, applyLookDelta)

  // Touch devices never use pointer lock: joystick moves, drag looks, tap
  // interacts (touchend preventDefault suppresses the synthesized click).
  const touch = touchScheme
    ? createTouchControls(getCanvas, applyLookDelta, () => tryInteract())
    : null

  const onCanvasClick = (): void => {
    if (!controlsEnabled || gameOver) return
    if (touchScheme) {
      tryInteract()
      return
    }
    if (!pointerLock.isLocked()) {
      pointerLock.request()
      return
    }
    tryInteract()
  }
  getCanvas()?.addEventListener('click', onCanvasClick)

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.code === 'KeyE' && !event.repeat && pointerLock.isLocked()) {
      tryInteract()
    }
  }
  window.addEventListener('keydown', onKeyDown)

  // --- Fixed-timestep simulation -------------------------------------------
  const stepMovement = (dt: number): void => {
    const stick = touch?.moveVector() ?? { forward: 0, strafe: 0 }
    const forwardInput =
      (keyboard.isDown('KeyW') ? 1 : 0) - (keyboard.isDown('KeyS') ? 1 : 0) + stick.forward
    const strafeInput =
      (keyboard.isDown('KeyD') ? 1 : 0) - (keyboard.isDown('KeyA') ? 1 : 0) + stick.strafe
    if (forwardInput === 0 && strafeInput === 0) return

    // Yaw 0 faces -Z; positive yaw turns left (Three.js YXZ convention).
    const sinYaw = Math.sin(pose.yaw)
    const cosYaw = Math.cos(pose.yaw)
    const dirX = forwardInput * -sinYaw + strafeInput * cosYaw
    const dirZ = forwardInput * -cosYaw + strafeInput * -sinYaw
    const magnitude = Math.hypot(dirX, dirZ)
    if (magnitude === 0) return

    // Analog joystick gives partial deflection a partial speed; keyboard's
    // unit inputs still resolve to full walk speed.
    const speedScale = Math.min(magnitude, 1)
    const step = (WALK_SPEED * dt * speedScale) / magnitude
    const attempted = { x: pose.x + dirX * step, z: pose.z + dirZ * step }
    const feetY = pose.y - PLAYER_EYE_HEIGHT
    const resolved = resolveCapsuleAgainstBoxes(
      attempted,
      { feetY, headY: feetY + CAPSULE_HEIGHT, radius: CAPSULE_RADIUS },
      solidBoxes
    )

    if (resolved.x !== pose.x || resolved.z !== pose.z) {
      pose.x = resolved.x
      pose.z = resolved.z
      poseDirty = true
      applyCameraFromPose()
      updateZone()
    }
  }

  loop.onUpdate((dt) => {
    cacheTimer += dt
    if (cacheTimer >= SCENE_CACHE_REFRESH_INTERVAL) {
      refreshSceneCaches()
    }

    if (movementAllowed()) {
      stepMovement(dt)
      highlight.set(probe.probe(camera, interactables))
    } else {
      highlight.set(null)
    }

    emitTimer += dt
    if (poseDirty && emitTimer >= MOVED_EMIT_INTERVAL) {
      emitMoved()
    }
  })

  // --- Bus subscriptions ----------------------------------------------------
  bus.on('title:dismissed', () => {
    controlsEnabled = true
    if (!touchScheme) pointerLock.request()
  })

  bus.on('ui:journal-toggle', ({ open }) => {
    journalOpen = open
    if (touchScheme) return
    if (open) {
      pointerLock.exit()
    } else if (controlsEnabled && !gameOver) {
      pointerLock.request()
    }
  })

  const lockControls = (): void => {
    gameOver = true
    highlight.set(null)
    pointerLock.exit()
  }
  bus.on('game:won', lockControls)
  bus.on('game:lost', lockControls)

  // --- Debug teleport (works at any time, defaults to current pose) ---------
  debug.registerTeleport((x, y, z, yaw = pose.yaw, pitch = pose.pitch) => {
    pose.x = x
    pose.y = y
    pose.z = z
    pose.yaw = yaw
    pose.pitch = Math.min(PITCH_LIMIT, Math.max(-PITCH_LIMIT, pitch))
    applyCameraFromPose()
    updateZone()
    emitMoved()
  })

  // --- Spawn ----------------------------------------------------------------
  refreshSceneCaches()
  applyCameraFromPose()
  emitMoved()
}
